import React, { ReactElement, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DownloadIcon from '@mui/icons-material/Download';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField
} from '@mui/material';
import { User } from 'types/User';

import NotificationSection, { NotificationType } from '../../components/NotificationSection/NotificationSection';
import { downloadExcelFromObjects } from '../../helpers/FileHelpers';
import { isNonNullable } from '../../helpers/TypingHelpers';
import { labelToTestId } from '../../tests/helpers/TestHelpers';
import { ObjectQueryState } from '../../types/Api';
import { BoxRes, ImportInfo } from '../../types/Box';
import { ApiError } from '../../types/Error';
import { ImportMismatch, ScanItemType, ScanItemTypeEnum, ScanRes } from '../../types/Scan';

import '../../styles/modals/ImportDevicesModal.less';

interface Props {
    closeMe: () => void;
    fetchImportEntityToValidate: (itemId: string, itemType: ScanItemType) => void;
    scanToImportRes: ObjectQueryState<ScanRes | undefined>;
    boxToImportRes: ObjectQueryState<BoxRes | undefined>;
    isPendingImport: boolean;
    handleImport: (
        importInfo: { typeToImport: ScanItemType; idToImport: string; loggerIdToVerify: string },
        isForceEnabled: boolean | undefined
    ) => void;
    importErrRes: ApiError | null;
    currentUser: User;
}

const ImportDevicesModal = ({
    closeMe,
    fetchImportEntityToValidate,
    scanToImportRes,
    boxToImportRes,
    isPendingImport,
    handleImport,
    importErrRes,
    currentUser
}: Props) => {
    const [formValues, setFormValues] = useState<ImportInfo>({
        typeToImport: ScanItemTypeEnum.SCAN,
        idToImport: '',
        loggerIdToVerify: ''
    });

    const [validationResults, setValidationResults] = useState<ValidationResult>();
    const [isAdditionallyConfirmed, setIsAdditionallyConfirmed] = useState(false);
    const [downloadErrorMsg, setDownloadErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        const scanReady = scanToImportRes.isFetched;
        const boxReady = boxToImportRes.isFetched;

        const readyToPreValidate = formValues.typeToImport === ScanItemTypeEnum.BOX ? boxReady : scanReady;

        if (readyToPreValidate) {
            const result = getPreValidationResult(scanToImportRes, boxToImportRes, formValues);
            setValidationResults(result);
        }
    }, [scanToImportRes.isFetched, scanToImportRes.data, boxToImportRes.isFetched, boxToImportRes.data]);

    useEffect(() => {
        if (importErrRes) {
            const errData = importErrRes.response?.data;
            const detailsData = errData?.details as
                | { data: { mismatches?: Array<ImportMismatch>; errorCode: string } }
                | undefined;
            const mismatches = detailsData?.data?.mismatches || [];

            if (!mismatches.length) {
                setValidationResults({
                    isValid: false,
                    notificationSection: (
                        <NotificationSection
                            type={NotificationType.ERROR}
                            mainContent={importErrRes?.response?.data?.message ?? 'An unexpected error occurred.'}
                            headline={'An error occurred!'}
                        />
                    )
                });
                return;
            }

            const isAdmin = currentUser.isAdmin ?? false;
            const errorHeadline = importErrRes.response?.data.message ?? 'An error occurred!';
            const errorFileName = `logger_conflicts_${formValues.typeToImport}_${formValues.idToImport}`;

            setValidationResults({
                isValid: false,
                notificationSection: (
                    <NotificationSection
                        type={isAdmin ? NotificationType.WARNING : NotificationType.ERROR}
                        headline={errorHeadline}
                        mainContent={`${mismatches.length} loggers are already linked to a newer scan or box. This may happen if
                            you're importing an old item or if loggers were removed from the current one.`}
                        footer={
                            <DownloadMismatchButton
                                fileContent={mismatches}
                                fileName={errorFileName}
                                setDownloadErrorMsg={setDownloadErrorMsg}
                            />
                        }
                    />
                ),
                isForceEnabled: isAdmin
            });
        }
    }, [importErrRes]);

    const handleFormValueChange = <T extends keyof typeof formValues>(key: T, value: (typeof formValues)[T]) => {
        setFormValues((prev) => ({ ...prev, [key]: value }));
    };

    const isFormValid = Boolean(formValues.idToImport && formValues.loggerIdToVerify && !scanToImportRes.isLoading);
    const isLoadingImportItemToValidate = scanToImportRes.isLoading || boxToImportRes.isLoading;
    const isError = scanToImportRes.isError || boxToImportRes.isError;
    const isForceEnabled = validationResults?.isForceEnabled && !validationResults.isValid;

    return (
        <Modal
            isOpen={true}
            ariaHideApp={false}
            className="modal import-devices-modal"
            overlayClassName="modal-overlay"
            onRequestClose={closeMe}
            shouldCloseOnOverlayClick={false}
        >
            <div className="modal-header" data-testid="modal-header">
                {validationResults?.isValid ? 'Validation Successful' : 'Import Devices'}
                <FontAwesomeIcon onClick={closeMe} className="close-icon" icon={['far', 'times']} id="dimgray" />
            </div>

            <div className="modal-body">
                {validationResults ? (
                    <>
                        {validationResults.notificationSection}

                        {downloadErrorMsg && (
                            <div className="margin-top-extra">
                                <NotificationSection
                                    type={NotificationType.WARNING}
                                    headline="Download Error"
                                    mainContent={downloadErrorMsg}
                                />
                            </div>
                        )}

                        {validationResults.isForceEnabled && (
                            <div className="margin-top-extra">
                                <Checkbox
                                    id="caution-check"
                                    checked={isAdditionallyConfirmed}
                                    onChange={() => setIsAdditionallyConfirmed(!isAdditionallyConfirmed)}
                                    sx={{ padding: 0, marginRight: '6px' }}
                                />
                                <FormLabel htmlFor="caution-check">
                                    I understand this action requires caution.
                                </FormLabel>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="info-text margin-bottom-extra">
                            <FontAwesomeIcon icon="info-circle" className="margin-right" />
                            <span>
                                Fields in <strong>bold</strong> are required!
                            </span>
                        </div>

                        <Box sx={{ marginBottom: '10px' }}>
                            <FormControl>
                                <FormLabel
                                    component="legend"
                                    sx={{ fontWeight: 'bold', color: '#484848', marginBottom: '-4px' }}
                                >
                                    Import Source Type
                                </FormLabel>
                                <RadioGroup
                                    aria-label="scan or box"
                                    name="radio-buttons-group"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        width: '100%'
                                    }}
                                    value={formValues.typeToImport}
                                    onChange={(e) =>
                                        handleFormValueChange('typeToImport', e.target.value as ScanItemType)
                                    }
                                >
                                    <FormControlLabel value={ScanItemTypeEnum.SCAN} control={<Radio />} label="Scan" />
                                    <FormControlLabel value={ScanItemTypeEnum.BOX} control={<Radio />} label="Box" />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        <Stack spacing={2}>
                            <Box>
                                <FormLabel
                                    component="legend"
                                    htmlFor="scan-item-id"
                                    sx={{ fontWeight: 'bold', color: '#484848' }}
                                >
                                    Import Source ID
                                </FormLabel>
                                <TextField
                                    id="scan-item-id"
                                    value={formValues.idToImport}
                                    onChange={(e) => handleFormValueChange('idToImport', e.target.value)}
                                    margin="normal"
                                    placeholder="Add Scan or Box ID"
                                    size="small"
                                    sx={{ marginTop: '3px' }}
                                    inputProps={{ maxLength: 16 }}
                                    fullWidth
                                    autoFocus
                                />
                            </Box>

                            <Box>
                                <FormLabel
                                    component="legend"
                                    htmlFor="verify-id"
                                    sx={{ fontWeight: 'bold', color: '#484848' }}
                                >
                                    Verify Logger ID
                                </FormLabel>
                                <TextField
                                    id="verify-id"
                                    value={formValues.loggerIdToVerify}
                                    onChange={(e) => handleFormValueChange('loggerIdToVerify', e.target.value)}
                                    margin="normal"
                                    sx={{ marginTop: '3px' }}
                                    placeholder="Add any Logger ID from import source"
                                    size="small"
                                    inputProps={{ maxLength: 16 }}
                                    fullWidth
                                />
                            </Box>
                        </Stack>
                    </>
                )}
            </div>

            <div className="modal-footer" data-testid="modal-footer">
                <Button variant="outlined" data-testid="close-btn" onClick={closeMe}>
                    Cancel
                </Button>

                {!validationResults && !isError && (
                    <LoadingButton
                        variant="contained"
                        loading={isLoadingImportItemToValidate}
                        disabled={!isFormValid}
                        onClick={() => fetchImportEntityToValidate(formValues.idToImport, formValues.typeToImport)}
                    >
                        Validate
                    </LoadingButton>
                )}

                {(validationResults?.isValid || isForceEnabled) && (
                    <LoadingButton
                        variant="contained"
                        color={validationResults.isForceEnabled ? 'warning' : 'success'}
                        loading={isPendingImport}
                        disabled={isForceEnabled && !isAdditionallyConfirmed}
                        onClick={() => {
                            handleImport(formValues, validationResults?.isForceEnabled);
                        }}
                    >
                        Import
                    </LoadingButton>
                )}
            </div>
        </Modal>
    );
};

type ValidationResult = { isValid: boolean; notificationSection: ReactElement; isForceEnabled?: boolean };

export const getPreValidationResult = (
    importScanRes: ObjectQueryState<ScanRes | undefined>,
    importBoxRes: ObjectQueryState<BoxRes | undefined>,
    formValues: ImportInfo
): ValidationResult => {
    const isBoxImport = formValues.typeToImport === 'BOX';
    const queryResult = isBoxImport ? importBoxRes : importScanRes;

    // Scan item can either be the scan to import or the parent scan of the box to import
    const scanItem = importScanRes.data;
    const boxItem = importBoxRes.data;
    const itemToImport = isBoxImport ? boxItem : scanItem;

    if (queryResult.isError || !itemToImport) {
        const status = queryResult.error?.response?.status;
        const errMsg = queryResult.error?.response?.data?.message;

        const isNotFound = status === 404;
        const notFoundSuffix =
            'The selected source type may be incorrect. Double-check your selection. If the issue persists, please contact a supervisor.';
        const adjustedErrMsg =
            isNotFound && errMsg ? `${errMsg} -- ${notFoundSuffix}` : (errMsg ?? 'An unexpected error occurred.');

        return {
            isValid: false,
            notificationSection: (
                <NotificationSection
                    type={NotificationType.ERROR}
                    headline="An error occurred!"
                    mainContent={adjustedErrMsg}
                />
            )
        };
    }

    if (!isBoxImport && scanItem?.isBoxScan) {
        return {
            isValid: false,
            notificationSection: (
                <NotificationSection
                    type={NotificationType.ERROR}
                    headline="Box Scan Detected"
                    mainContent={`The imported item (ID: ${itemToImport.id}), is a box scan, which cannot be imported.`}
                />
            )
        };
    }
    if (isBoxImport && scanItem?.statusName === 'IN_PROGRESS') {
        return {
            isValid: false,
            notificationSection: (
                <NotificationSection
                    type={NotificationType.ERROR}
                    headline="Box Scan In Progress"
                    mainContent={`The scan (ID: ${scanItem.id}) containing the box to import is not complete.`}
                />
            )
        };
    }

    if (itemToImport.statusName === 'IN_PROGRESS') {
        return {
            isValid: false,
            notificationSection: (
                <NotificationSection
                    type={NotificationType.ERROR}
                    headline="Still in progress"
                    mainContent={`The imported item (ID: ${itemToImport.id}) is still in progress!`}
                />
            )
        };
    } else if (!itemToImport.loggers?.length) {
        return {
            isValid: false,
            notificationSection: (
                <NotificationSection
                    type={NotificationType.ERROR}
                    headline="No loggers found"
                    mainContent={`The imported item (ID: ${itemToImport.id}) does not contain any loggers. Please make sure you have the correct ID and try again.`}
                />
            )
        };
    }

    const customersInScan = itemToImport.loggers
        .map((logger) => logger.customer.name)
        .filter((name) => isNonNullable(name));

    return {
        isValid: true,
        notificationSection: (
            <NotificationSection
                type={NotificationType.INFO}
                headline={`${isBoxImport ? 'Box' : 'Scan'} to Import`}
                mainContent={
                    <div className="validation-info-container">
                        <InfoRow label={`${isBoxImport ? 'Box' : 'Scan'} ID`} value={itemToImport.id} />
                        {isBoxImport && <InfoRow label="Parent Scan ID" value={scanItem?.id} />}
                        <InfoRow label="Source Location" value={scanItem?.locationName} />
                        <InfoRow label="Logger's Assigned Customer" value={getCustomerInfo(customersInScan)} />
                        <InfoRow label="Logger ID to Verify" value={formValues.loggerIdToVerify} />
                        <InfoRow label="Logger Count" value={itemToImport.loggers?.length} />
                        <InfoRow label="Tracking ID" value={scanItem?.trackingId} />
                        <InfoRow label="Sales Order ID" value={scanItem?.salesOrderId} />
                    </div>
                }
            />
        )
    };
};

export const getCustomerInfo = (distinctCustomers: Array<string>): string => {
    if (distinctCustomers.length === 0) return '–';
    if (distinctCustomers.length === 1) return distinctCustomers[0];
    return 'Multiple';
};

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div data-testid={labelToTestId(label)} className="validation-info-row">
        <span>{label}</span>
        <span className="value">{value || '-'}</span>
    </div>
);

interface DownloadProps {
    fileContent: Array<ImportMismatch>;
    fileName: string;
    setDownloadErrorMsg: (message: string | null) => void;
}

const DownloadMismatchButton = ({ fileContent, fileName, setDownloadErrorMsg }: DownloadProps) => (
    <button
        data-testid="download-excel-file-btn"
        title="Download Excel File"
        className="download-mismatch-button no-style"
        onClick={() => {
            const { error } = downloadExcelFromObjects(fileContent, fileName, 'Import Mismatches');
            if (error) {
                console.error('Download failed:', error);
                setDownloadErrorMsg(error);
            }
        }}
    >
        <DownloadIcon />
        <div>Download logger conflicts</div>
    </button>
);

export default ImportDevicesModal;
